import React, { useMemo, useState } from "react";
import {
  Row,
  Col,
  Table,
  Badge,
  ProgressBar,
  Pagination,
} from "react-bootstrap";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
  ComposedChart,
} from "recharts";
import { Kpi } from "./Kpi";

import {
  monthlyRevenueByStore,
  monthlyRevenueByCategory,
  monthlyTotals,
  agentPerformance,
  paymentMix,
  hourlyDistribution,
  dayOfWeek,
  getTopProducts,
} from "../../../lib/mockSales";
import { ChartCard } from "../../../components/dashboard/ChartCard";
import { convertDate, fmtMoney } from "../../../methods/commonMethods";

const PALETTE = [
  "#4f8cff",
  "#7c5cff",
  "#21c997",
  "#ffb020",
  "#ff6b6b",
  "#26c6da",
  "#ab47bc",
];

/**
 * @param {number} n
 * @returns {string}
 */
function fmtCompact(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
}

/**
 * @typedef {Object} SalesReportProps
 * @property {import("../../../components/dashboard/Sidebar").ReportKey} report
 * @property {number} months
 * @property {string} startDate
 * @property {string} endDate
 * @property {string} store
 * @property {string} salesagent
 * @property {import("../../../models/StoreModel").StoreModel[]} stores
 * @property {import("../../../models/SalesAgentsModel").SalesAgentModel[]} salesagents
 * @property {import("../../../models/CategoryModel").CategoryModel[]} categories
 * @property {import("../../../models/SalesModel").SalesModel[]} sales
 * @property {import("../../../models/InvoiceModel").InvoiceModel[]} invoices
 */

/**
 * @param {SalesReportProps} props
 */
export function SalesReport({
  report,
  startDate,
  endDate,
  store,
  salesagent,
  stores,
  salesagents,
  categories,
  sales,
  invoices,
}) {
  const [currentPage, setCurrentPage] = useState(1);

  // console.log(endDate);
  const totals = useMemo(
    () => monthlyTotals(startDate, endDate, invoices, sales),
    [stores, sales],
  );
  const byStore = useMemo(
    () => monthlyRevenueByStore(startDate, endDate, stores, sales),
    [stores, sales],
  );
  const byCat = useMemo(
    () => monthlyRevenueByCategory(startDate, endDate, categories, sales),
    [categories, sales],
  );
  const agents = useMemo(
    () => agentPerformance(startDate, endDate, salesagents, invoices),
    [salesagents, invoices],
  );
  const payments = useMemo(
    () => paymentMix(startDate, endDate, invoices),
    [invoices],
  );

  const topProducts = useMemo(
    () => getTopProducts(startDate, endDate, sales, categories),
    [categories, sales],
  );

  const hourly = useMemo(
    () => hourlyDistribution(startDate, endDate, sales),
    [sales],
  );
  const dow = useMemo(() => dayOfWeek(startDate, endDate, sales), [sales]);

  const grand = totals.reduce((s, r) => s + r.revenue, 0);
  const orders = totals.reduce((s, r) => s + r.orders, 0);
  const avgBasket = orders ? Math.round(grand / orders) : 0;
  const last = totals[totals.length - 1];
  const prev = totals[totals.length - 2];
  const mom =
    last && prev
      ? Math.round(((last.revenue - prev.revenue) / prev.revenue) * 1000) / 10
      : 0;

  // MoM delta series
  const momSeries = totals.map((r, i) => ({
    month: r.month,
    revenue: r.revenue,
    prev: i > 0 ? totals[i - 1].revenue : r.revenue,
    delta: i > 0 ? r.revenue - totals[i - 1].revenue : 0,
  }));

  const storeTotals = stores
    .map((s) => ({
      name: s.storename,
      revenue: byStore.reduce(
        (sum, r) => sum + /** @type {number} */ (r[s.storename]),
        0,
      ),
    }))
    .sort((a, b) => b.revenue - a.revenue);

  const catTotals = categories
    .map((c) => ({
      name: c.name,
      revenue: byCat.reduce(
        (sum, r) => sum + /** @type {number} */ (r[c.name]),
        0,
      ),
    }))
    .sort((a, b) => b.revenue - a.revenue);

  const filterNote =
    store === "all"
      ? `All stores`
      : `${stores.find((s, i) => s.storecode === Number(store)).storename}`;

  const perPage = 50;

  const totalPages = Math.ceil(invoices.length / perPage);
  const paginatedInvoices = invoices.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage,
  );

  if (report === "sales-overview") {
    return (
      <>
        <Row className="g-3 mb-3">
          <Col md={6} xl={3}>
            <Kpi
              label="Total Revenue"
              value={fmtMoney(grand)}
              delta={`${mom >= 0 ? "+" : ""}${mom}% MoM`}
              icon="bi-cash-stack"
              tone="success"
            />
          </Col>
          <Col md={6} xl={3}>
            <Kpi
              label="Orders"
              value={orders.toLocaleString()}
              delta="+8.2%"
              icon="bi-bag-check-fill"
              tone="info"
            />
          </Col>
          <Col md={6} xl={3}>
            <Kpi
              label="Avg Basket"
              value={fmtMoney(avgBasket)}
              delta="+1.4%"
              icon="bi-basket-fill"
              tone="primary"
            />
          </Col>
          <Col md={6} xl={3}>
            <Kpi
              label="Active Stores"
              value={String(stores.length)}
              delta={filterNote}
              icon="bi-shop"
              tone="warning"
            />
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col xl={8}>
            <ChartCard
              title="Revenue vs Previous Month"
              subtitle="Month-over-month comparison"
            >
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={momSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                  <XAxis dataKey="month" stroke="#7a8395" fontSize={12} />
                  <YAxis
                    stroke="#7a8395"
                    fontSize={12}
                    tickFormatter={fmtCompact}
                  />
                  <Tooltip
                    formatter={(v) => fmtMoney(Number(v) || 0)}
                    wrapperStyle={{
                      fontSize: "11px",
                      fontWeight: "bold",
                      zIndex: 10,
                    }}
                  />
                  <Legend />
                  <Bar
                    dataKey="prev"
                    name="Previous"
                    fill="#c9d1e0"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="revenue"
                    name="Current"
                    fill="#4f8cff"
                    radius={[4, 4, 0, 0]}
                  />
                  <Line
                    type="monotone"
                    dataKey="delta"
                    name="Δ"
                    stroke="#ff6b6b"
                    strokeWidth={2}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
          <Col xl={4}>
            <ChartCard title="Payment Mix" subtitle="Share of revenue">
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={payments}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={55}
                    outerRadius={100}
                    paddingAngle={2}
                  >
                    {payments.map((_, i) => (
                      <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v) => fmtMoney(Number(v) || 0)}
                    wrapperStyle={{
                      fontSize: "10px",
                      fontWeight: "bold",
                      zIndex: 10,
                    }}
                  />
                  <Legend
                    wrapperStyle={{
                      fontSize: "11px",
                      fontWeight: "bold",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
        </Row>

        <Row className="g-3">
          <Col xl={6}>
            <ChartCard
              title="Revenue by Store"
              subtitle="Contribution across outlets"
            >
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={storeTotals} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                  <XAxis
                    type="number"
                    stroke="#7a8395"
                    fontSize={12}
                    tickFormatter={fmtCompact}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    stroke="#7a8395"
                    fontSize={12}
                    width={100}
                  />
                  <Tooltip
                    formatter={(v) => fmtMoney(Number(v) || 0)}
                    wrapperStyle={{
                      fontSize: "10px",
                      fontWeight: "bold",
                      zIndex: 10,
                    }}
                  />
                  <Bar dataKey="revenue" fill="#7c5cff" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
          <Col xl={6}>
            <ChartCard title="Revenue by Category" subtitle="Product mix">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={catTotals} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                  <XAxis
                    type="number"
                    stroke="#7a8395"
                    fontSize={12}
                    tickFormatter={fmtCompact}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    stroke="#7a8395"
                    fontSize={12}
                    width={120}
                  />
                  <Tooltip
                    formatter={(v) => fmtMoney(Number(v) || 0)}
                    wrapperStyle={{
                      fontSize: "10px",
                      fontWeight: "bold",
                      zIndex: 10,
                    }}
                  />
                  <Bar dataKey="revenue" fill="#21c997" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
        </Row>
      </>
    );
  }

  if (report === "sales-record") {
    const totalAmount = (invoices ?? []).reduce((s, r) => s + r.sttlsales, 0);
    const taxAmount = (invoices ?? []).reduce((s, r) => s + r.invtaxamount, 0);
    const reversalsAmount = (invoices ?? []).reduce(
      (s, r) => s + (r.transtype === "SalesReversal" ? r.sttlsales : 0),
      0,
    );
    const discAmount = (invoices ?? []).reduce((s, r) => s + r.discount, 0);
    // discount has already been minused from sttlsales
    const netTotal = totalAmount - taxAmount;
    const averageSale = netTotal / (invoices ?? []).length;
    return (
      <>
        <Row className="g-3 mb-3">
          <Col md={4}>
            <Kpi
              label="Entries"
              value={(invoices ?? []).length}
              icon="bi-boxes"
              tone="info"
            />
          </Col>
          <Col md={4}>
            <Kpi
              label="Total (Ksh)"
              value={fmtMoney(totalAmount, false)}
              icon="bi-cash-stack"
              tone="primary"
            />
          </Col>
          <Col md={4}>
            <Kpi
              label="Taxes (Ksh)"
              value={fmtMoney(taxAmount, false)}
              icon="bi-receipt"
              tone="warning"
            />
          </Col>
          <Col md={3}>
            <Kpi
              label="Reversals (Ksh)"
              value={fmtMoney(reversalsAmount, false)}
              icon="bi-arrow-counterclockwise"
              tone="danger"
            />
          </Col>
          <Col md={3}>
            <Kpi
              label="Discount (Ksh)"
              value={fmtMoney(discAmount, false)}
              icon="bi-receipt-cutoff"
              tone="danger"
            />
          </Col>
          <Col md={3}>
            <Kpi
              label="Net Total (Ksh)"
              value={fmtMoney(netTotal, false)}
              icon="bi-graph-up-arrow"
              tone="success"
            />
          </Col>
          <Col md={3}>
            <Kpi
              label="Avg. Sale (Ksh)"
              value={fmtMoney(averageSale, false)}
              // value={0}
              icon="bi-calculator"
              tone="secondary"
            />
          </Col>
        </Row>
        <Row className="g-3">
          <Col xl={12}>
            <ChartCard title="View Sales Entries" subtitle="">
              <div className="pbi-chart-body">
                <div style={{ maxHeight: 400, overflow: "auto" }}>
                  <Table
                    hover
                    responsive
                    striped
                    bordered
                    size="sm"
                    className="pbi-table mb-0"
                    style={{ tableLayout: "fixed", width: "100%" }}
                  >
                    <thead className="sticky-top bg-white">
                      <tr>
                        <th>Date</th>
                        <th>Customer Name</th>
                        <th>Sales Type</th>
                        <th>Receipt No.</th>
                        <th>Total Sales (Ksh)</th>
                        <th>Tax (Ksh)</th>
                        <th>Paid (Ksh)</th>
                        <th>Balance (Ksh)</th>
                        <th>Trans Type</th>
                        <th>Sales Person</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* {(invoices || []).map((s, index) => { */}
                      {(paginatedInvoices || []).map((s, index) => {
                        return (
                          <tr key={index}>
                            <td className="text-muted">
                              {convertDate(s?.sdate)}
                            </td>
                            <td className="text-muted">{s?.custname}</td>
                            <td className="text-muted">{s?.vchtype}</td>
                            <td className="text-muted">{s?.auto1}</td>
                            <td className="text-muted">
                              {fmtMoney(s?.sttlsales, false)}
                            </td>
                            <td className="text-muted">
                              {fmtMoney(s?.invtaxamount, false)}
                            </td>
                            <td className="text-muted">
                              {fmtMoney(s?.amount, false)}
                            </td>
                            <td className="text-muted">
                              {fmtMoney(s?.balance, false)}
                            </td>
                            <td className="text-muted">{s?.transtype}</td>
                            <td className="text-muted">{s?.salesagentname}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
                <Pagination className="mt-2">
                  <Pagination.Prev
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                  />
                  <Pagination.Item>
                    {currentPage} / {totalPages}
                  </Pagination.Item>

                  <Pagination.Next
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => p + 1)}
                  />
                </Pagination>
              </div>
            </ChartCard>
          </Col>
        </Row>
      </>
    );
  }

  if (report === "sales-by-store") {
    /** @type {("primary"|"success"|"info"|"warning")[]} */
    const tones = ["primary", "success", "info", "warning"];
    return (
      <>
        <Row className="g-3 mb-3">
          {storeTotals.slice(0, 4).map((s, i) => (
            <Col md={6} xl={3} key={s.name}>
              <Kpi
                label={s.name}
                value={fmtMoney(s.revenue)}
                delta={`${Math.round((s.revenue / grand) * 100)}% of total`}
                icon="bi-shop"
                tone={tones[i % 4]}
              />
            </Col>
          ))}
        </Row>
        <Row className="g-3 mb-3">
          <Col xl={12}>
            <ChartCard
              title="Monthly Revenue by Store"
              subtitle="Stacked contribution over time"
            >
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={byStore}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                  <XAxis dataKey="month" stroke="#7a8395" fontSize={12} />
                  <YAxis
                    stroke="#7a8395"
                    fontSize={12}
                    tickFormatter={fmtCompact}
                  />
                  <Tooltip
                    formatter={(v) => fmtMoney(Number(v) || 0)}
                    wrapperStyle={{
                      fontSize: "11px",
                      fontWeight: "bold",
                      zIndex: 10,
                    }}
                  />
                  <Legend
                    wrapperStyle={{
                      fontSize: "11px",
                      fontWeight: "bold",
                    }}
                  />
                  {stores.map((s, i) => (
                    <Bar
                      key={s.storename}
                      dataKey={s.storename}
                      stackId="a"
                      fill={PALETTE[i % PALETTE.length]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
        </Row>
        <Row className="g-3">
          <Col xl={12}>
            <ChartCard title="Store Performance" subtitle="Ranked by revenue">
              <Table hover responsive size="sm" className="pbi-table mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Store</th>
                    <th className="text-end">Revenue (Ksh)</th>
                    <th className="text-end">% Share</th>
                    <th style={{ width: 240 }}>Contribution</th>
                  </tr>
                </thead>
                <tbody>
                  {storeTotals.map((s, i) => {
                    const share = Math.round((s.revenue / grand) * 100);
                    return (
                      <tr key={s.name}>
                        <td className="text-muted">{i + 1}</td>
                        <td className="fw-semibold">{s.name}</td>
                        <td className="text-end">
                          {fmtMoney(s.revenue, false)}
                        </td>
                        <td className="text-end">{share}%</td>
                        <td>
                          <ProgressBar
                            now={share}
                            variant={i === 0 ? "success" : "info"}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </ChartCard>
          </Col>
        </Row>
      </>
    );
  }

  if (report === "sales-by-category") {
    const totalCat = catTotals.reduce((s, r) => s + r.revenue, 0);
    return (
      <>
        <Row className="g-3 mb-3">
          <Col md={4}>
            <Kpi
              label="Top Category"
              value={catTotals[0]?.name ?? ""}
              delta={fmtMoney(catTotals[0]?.revenue ?? 0)}
              icon="bi-trophy-fill"
              tone="success"
            />
          </Col>
          <Col md={4}>
            <Kpi
              label="Categories"
              value={String(categories.length)}
              icon="bi-tags-fill"
              tone="info"
            />
          </Col>
          <Col md={4}>
            <Kpi
              label="Total Sales"
              value={fmtMoney(totalCat)}
              delta={filterNote}
              icon="bi-cash-stack"
              tone="primary"
            />
          </Col>
        </Row>
        <Row className="g-3 mb-3">
          <Col xl={7}>
            <ChartCard
              title="Category Trend"
              subtitle="Monthly revenue per category"
            >
              <ResponsiveContainer width="100%" height={390}>
                <LineChart data={byCat}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                  <XAxis dataKey="month" stroke="#7a8395" fontSize={12} />
                  <YAxis
                    stroke="#7a8395"
                    fontSize={12}
                    tickFormatter={fmtCompact}
                  />
                  <Tooltip
                    formatter={(v) => fmtMoney(Number(v) || 0)}
                    wrapperStyle={{
                      fontSize: "11px",
                      fontWeight: "bold",
                      zIndex: 10,
                    }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: "11px", fontWeight: "bold" }}
                  />
                  {categories.map((c, i) => (
                    <Line
                      key={c.name}
                      type="monotone"
                      dataKey={c.name}
                      stroke={PALETTE[i % PALETTE.length]}
                      strokeWidth={2}
                      dot={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
          <Col xl={5}>
            <ChartCard title="Category Share" subtitle="% of revenue">
              <ResponsiveContainer width="100%" height={390}>
                <PieChart>
                  <Pie
                    data={catTotals}
                    dataKey="revenue"
                    nameKey="name"
                    outerRadius={120}
                    label={(e) =>
                      `${Math.round((e.revenue / totalCat) * 100)}%`
                    }
                  >
                    {catTotals.map((_, i) => (
                      <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v) => fmtMoney(Number(v) || 0)}
                    wrapperStyle={{
                      fontSize: "10px",
                      fontWeight: "bold",
                    }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: "11px", fontWeight: "bold" }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
        </Row>
      </>
    );
  }

  if (report === "sales-by-agent") {
    const topAgent = agents[0];
    const totalAgentRev = agents.reduce((s, a) => s + a.revenue, 0);
    return (
      <>
        <Row className="g-3 mb-3">
          <Col md={4}>
            <Kpi
              label="Top Agent"
              value={topAgent?.employeesname}
              delta={fmtMoney(topAgent?.revenue)}
              icon="bi-award-fill"
              tone="success"
            />
          </Col>
          <Col md={4}>
            <Kpi
              label="Agents Reporting"
              value={String((agents ?? []).length)}
              icon="bi-person-badge-fill"
              tone="info"
            />
          </Col>
          <Col md={4}>
            <Kpi
              label="Team Revenue"
              value={fmtMoney(totalAgentRev)}
              delta={filterNote}
              icon="bi-people-fill"
              tone="primary"
            />
          </Col>
        </Row>
        {/* <Row className="g-3 mb-3">
          <Col xl={12}>
            <ChartCard
              title="Agent Revenue vs Target"
              subtitle="Attainment across sales team"
            >
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={agents}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                  <XAxis
                    dataKey="name"
                    stroke="#7a8395"
                    fontSize={11}
                    interval={0}
                    angle={-15}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis
                    stroke="#7a8395"
                    fontSize={12}
                    tickFormatter={fmtCompact}
                  />
                  <Tooltip formatter={(v) => fmtMoney(Number(v) || 0)} />
                  <Legend />
                  <Bar
                    dataKey="target"
                    name="Target"
                    fill="#c9d1e0"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="revenue"
                    name="Actual"
                    fill="#4f8cff"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
        </Row> */}
        <Row className="g-3">
          <Col xl={12}>
            <ChartCard title="Sales Agents Leaderboard">
              <Table hover responsive size="sm" className="pbi-table mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Agent</th>
                    {/* <th>Store</th> */}
                    <th className="text-end">Orders</th>
                    <th className="text-end">Avg Basket (Ksh)</th>
                    <th className="text-end">Revenue (Ksh)</th>
                    {/* <th className="text-end">Target</th>
                    <th>Attainment</th> */}
                  </tr>
                </thead>
                <tbody>
                  {agents.map((a, i) => (
                    <tr key={a.employeescode}>
                      <td className="text-muted">{i + 1}</td>
                      <td className="fw-semibold">{a.employeesname}</td>
                      {/* <td>{a.store}</td> */}
                      <td className="text-end">{a.orders.toLocaleString()}</td>
                      <td className="text-end">
                        {fmtMoney(a.avgBasket, false)}
                      </td>
                      <td className="text-end">{fmtMoney(a.revenue, false)}</td>
                      {/* <td className="text-end text-muted">
                        {fmtMoney(a.target)}
                      </td>
                      <td style={{ minWidth: 160 }}>
                        <div className="d-flex align-items-center gap-2">
                          <ProgressBar
                            now={Math.min(a.attainment, 130)}
                            variant={
                              a.attainment >= 100
                                ? "success"
                                : a.attainment >= 80
                                  ? "info"
                                  : "warning"
                            }
                            style={{ flex: 1, height: 8 }}
                          />
                          <Badge
                            bg={a.attainment >= 100 ? "success" : "secondary"}
                          >
                            {a.attainment}%
                          </Badge>
                        </div>
                      </td> */}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </ChartCard>
          </Col>
        </Row>
      </>
    );
  }

  if (report === "sales-top-products") {
    const topRev = topProducts[0];
    return (
      <>
        <Row className="g-3 mb-3">
          <Col md={4}>
            <Kpi
              label="Best Seller"
              value={topRev?.name ?? ""}
              delta={`${(topRev?.unitsSold ?? 0).toLocaleString()} units`}
              icon="bi-trophy-fill"
              tone="success"
            />
          </Col>
          <Col md={4}>
            <Kpi
              label="Products Tracked"
              value={String((topProducts ?? []).length)}
              icon="bi-box2-fill"
              tone="info"
            />
          </Col>
          <Col md={4}>
            <Kpi
              label="Revenue (top 10)"
              value={fmtMoney(topProducts.reduce((s, p) => s + p.revenue, 0))}
              icon="bi-cash-stack"
              tone="primary"
            />
          </Col>
        </Row>
        <Row className="g-3 mb-3">
          <Col xl={7}>
            <ChartCard title="Top Products by Revenue">
              <ResponsiveContainer width="100%" height={380}>
                <BarChart data={topProducts} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                  <XAxis
                    type="number"
                    stroke="#7a8395"
                    fontSize={12}
                    tickFormatter={fmtCompact}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    stroke="#7a8395"
                    fontSize={11}
                    width={170}
                  />
                  <Tooltip
                    formatter={(v) => fmtMoney(Number(v) || 0)}
                    wrapperStyle={{
                      fontSize: "10px",
                      fontWeight: "bold",
                      zIndex: 10,
                    }}
                  />
                  <Bar dataKey="revenue" fill="#4f8cff" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
          <Col xl={5}>
            <ChartCard title="Units Sold">
              <ResponsiveContainer width="100%" height={380}>
                <BarChart data={topProducts} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                  <XAxis type="number" stroke="#7a8395" fontSize={12} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    stroke="#7a8395"
                    fontSize={11}
                    width={170}
                  />
                  <Tooltip
                    wrapperStyle={{
                      fontSize: "10px",
                      fontWeight: "bold",
                      zIndex: 10,
                    }}
                  />
                  <Bar
                    dataKey="unitsSold"
                    fill="#21c997"
                    radius={[0, 6, 6, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
        </Row>
      </>
    );
  }

  if (report === "sales-hourly") {
    // const hourly = hourlyDistribution();
    // const dow = dayOfWeek();
    const peak = hourly.reduce(
      (m, h) => (h.revenue > m.revenue ? h : m),
      hourly[0],
    );
    return (
      <>
        <Row className="g-3 mb-3">
          <Col md={4}>
            <Kpi
              label="Peak Hour"
              value={peak.hour}
              delta={fmtMoney(peak.revenue)}
              icon="bi-clock-fill"
              tone="success"
            />
          </Col>
          <Col md={4}>
            <Kpi
              label="Busiest Day"
              value={
                dow.reduce((m, d) => (d.revenue > m.revenue ? d : m), dow[0])
                  .day
              }
              icon="bi-calendar-week-fill"
              tone="info"
            />
          </Col>
          <Col md={4}>
            <Kpi
              label="Avg Daily Revenue"
              value={fmtMoney(
                Math.round(dow.reduce((s, d) => s + d.revenue, 0) / 7),
              )}
              icon="bi-graph-up"
              tone="primary"
            />
          </Col>
        </Row>
        <Row className="g-3">
          <Col xl={7}>
            <ChartCard title="Sales by Hour of Day">
              <ResponsiveContainer width="100%" height={320}>
                <AreaChart data={hourly}>
                  <defs>
                    <linearGradient id="gHour" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#7c5cff" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#7c5cff" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                  <XAxis dataKey="hour" stroke="#7a8395" fontSize={12} />
                  <YAxis
                    stroke="#7a8395"
                    fontSize={12}
                    tickFormatter={fmtCompact}
                  />
                  <Tooltip
                    formatter={(v) => fmtMoney(Number(v) || 0)}
                    wrapperStyle={{
                      fontSize: "10px",
                      fontWeight: "bold",
                      zIndex: 10,
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#7c5cff"
                    fill="url(#gHour)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
          <Col xl={5}>
            <ChartCard title="Sales by Day of Week">
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={dow}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                  <XAxis dataKey="day" stroke="#7a8395" fontSize={12} />
                  <YAxis
                    stroke="#7a8395"
                    fontSize={12}
                    tickFormatter={fmtCompact}
                  />
                  <Tooltip
                    formatter={(v) => fmtMoney(Number(v) || 0)}
                    wrapperStyle={{
                      fontSize: "10px",
                      fontWeight: "bold",
                      zIndex: 10,
                    }}
                  />
                  <Bar dataKey="revenue" fill="#21c997" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
        </Row>
      </>
    );
  }

  // sales-payments
  /** @type {("primary"|"success"|"info"|"warning")[]} */
  const kpiTones = ["primary", "success", "info", "warning"];
  /** @type {("primary"|"success"|"info"|"warning"|"danger")[]} */
  const barTones = ["primary", "success", "info", "warning", "danger"];
  const totalPay = payments.reduce((s, p) => s + p.value, 0);
  return (
    <>
      <Row className="g-3 mb-3">
        {payments.slice(0, 4).map((p, i) => (
          <Col md={6} xl={3} key={p.name}>
            <Kpi
              label={p.name}
              value={fmtMoney(p.value)}
              delta={`${Math.round((p.value / totalPay) * 100)}% of revenue`}
              icon="bi-credit-card-fill"
              tone={kpiTones[i % 4]}
            />
          </Col>
        ))}
      </Row>
      <Row className="g-3">
        <Col xl={6}>
          <ChartCard title="Payment Method Mix">
            <ResponsiveContainer width="100%" height={360}>
              <PieChart>
                <Pie
                  data={payments}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={60}
                  outerRadius={120}
                  paddingAngle={2}
                >
                  {payments.map((_, i) => (
                    <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v) => fmtMoney(Number(v) || 0)}
                  wrapperStyle={{
                    fontSize: "10px",
                    fontWeight: "bold",
                    zIndex: 10,
                  }}
                />
                <Legend
                  wrapperStyle={{
                    fontSize: "10px",
                    fontWeight: "bold",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Col>
        <Col xl={6}>
          <ChartCard title="Payment Breakdown">
            <Table hover responsive size="sm" className="pbi-table mb-0">
              <thead>
                <tr>
                  <th>Method</th>
                  <th className="text-end">Revenue (Ksh)</th>
                  <th className="text-end">% Share</th>
                  <th style={{ width: 200 }}></th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p, i) => {
                  const share = Math.round((p.value / totalPay) * 100);
                  return (
                    <tr key={p.name}>
                      <td className="fw-semibold">{p.name}</td>
                      <td className="text-end">{fmtMoney(p.value, false)}</td>
                      <td className="text-end">{share}%</td>
                      <td>
                        <ProgressBar
                          now={share}
                          style={{ height: 8 }}
                          variant={barTones[i % 5]}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </ChartCard>
        </Col>
      </Row>
    </>
  );
}
